import { Activity } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { Card } from "../../primitives/card";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import {
  formatCurrencyGbpCompact,
  formatCount,
  formatTimeOrDash,
  formatWeekdayOrDash,
} from "@shared-core/domain/formatters";
import { ClickableStat, IntentCardHeader } from "./IntentCardPrimitives";
import { CustomerHealthBadge } from "./CustomerHealthBadge";
import { accountHealthScoreFromSignals } from "./strategyTiers";

interface CustomerIdentityKpisProps {
  customer: API.CustomerIntelligenceDetail;
  liveEstimateCount: number;
  liveEstimateValue: number;
  liveJobCount: number;
  liveJobValue: number;
  daysSinceLastJob: number | null;
  nextMeetingAt: string | null;
  openTicketCount: number;
}

/**
 * Pulse card — short-term activity summary. Hero revenue KPI + five
 * clickable sub-KPIs for today's activity. The Strategy pyramid
 * (Opportunity / Relationship depth / Account health) is a separate
 * component mounted beneath this one.
 */
export function CustomerIdentityKpis({
  customer,
  liveEstimateCount,
  liveEstimateValue,
  liveJobCount,
  liveJobValue,
  daysSinceLastJob,
  nextMeetingAt,
  openTicketCount,
}: CustomerIdentityKpisProps) {
  const customerId = customer.customer.id;
  const metrics = customer.metrics;
  const healthScore = accountHealthScoreFromSignals(customer.signals);
  const healthOverride = customer.health_tier_override;
  const revenue30d = metrics?.revenue_last_30_days ?? 0;
  const bookedCount30d = metrics?.order_count_last_30_days ?? 0;

  const nextMeetingLabel = nextMeetingAt
    ? `${formatWeekdayOrDash(nextMeetingAt)} ${formatTimeOrDash(nextMeetingAt)}`
    : null;

  return (
    // Named region, like the Billing group / Improvement logs / Campaign
    // history / Contacts sections further down the page: without it this
    // card is unreachable by landmark and the page reads as one block.
    <Card className="p-5 gap-3" role="region" aria-label="Pulse">
      <IntentCardHeader
        label={
          <span className="inline-flex items-center gap-1.5">
            <Activity className="size-4" />
            Pulse
            <CustomerHealthBadge
              score={healthScore}
              showScore
              overrideTier={healthOverride?.override_tier}
              isOverridden={Boolean(healthOverride)}
            />
          </span>
        }
        hero={
          <span>
            {formatCurrencyGbpCompact(revenue30d)}{" "}
            <span className="text-base text-muted-foreground font-normal">
              booked last 30d
            </span>
          </span>
        }
        subtitle={
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span>
              <span className="font-medium text-foreground">
                {bookedCount30d}
              </span>{" "}
              booked
            </span>
          </span>
        }
        icon={<Activity className="size-5" />}
        tone="primary"
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 pt-3 border-t border-border">
        <ClickableStat
          label="Live estimates"
          value={
            liveEstimateValue > 0
              ? formatCurrencyGbpCompact(liveEstimateValue)
              : "—"
          }
          sub={`${formatCount(liveEstimateCount)} open`}
          to={paths.estimates({ customerId, status: "open" })}
          muted={liveEstimateCount === 0}
        />
        <ClickableStat
          label="Live jobs"
          value={
            liveJobValue > 0 ? formatCurrencyGbpCompact(liveJobValue) : "—"
          }
          sub={`${formatCount(liveJobCount)} in production`}
          // The figure is worth showing on a customer page regardless; only the
          // link into `production_ops` / `communications` goes when the feature
          // is not live here.
          to={isRouteEnabled("jobs") ? paths.jobs({ customerId }) : undefined}
          muted={liveJobCount === 0}
        />
        <ClickableStat
          label="Last job"
          value={daysSinceLastJob != null ? `${daysSinceLastJob}d` : "—"}
          sub={daysSinceLastJob != null ? "ago" : undefined}
          to={isRouteEnabled("jobs") ? paths.jobs({ customerId }) : undefined}
          muted={daysSinceLastJob == null}
        />
        <ClickableStat
          label="Next meeting"
          value={nextMeetingLabel ?? "—"}
          to={
            isRouteEnabled("communications")
              ? `${paths.communications()}?customer=${customerId}`
              : undefined
          }
          muted={!nextMeetingLabel}
        />
        <ClickableStat
          label="Open tickets"
          value={formatCount(openTicketCount)}
          to={`${paths.tickets()}?customer=${customerId}`}
          muted={openTicketCount === 0}
        />
      </div>
    </Card>
  );
}
